import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import api, { formatApiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";
import PageHeader from "@/components/PageHeader";
import { SwishPricing } from "@/components/SwishPricing";
import PaymentHistory from "@/components/PaymentHistory";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Switch } from "@/components/ui/switch";
import { Label } from "@/components/ui/label";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle, AlertDialogTrigger,
} from "@/components/ui/alert-dialog";
import { Input } from "@/components/ui/input";
import { Download, Trash2, ShieldCheck, Pencil, FileCheck2, Lock, BellRing, Ticket, LogOut, Briefcase } from "lucide-react";

export default function Installningar() {
  const { logout, logoutAll } = useAuth();
  const navigate = useNavigate();
  const [busy, setBusy] = useState(false);
  const [loggingOutAll, setLoggingOutAll] = useState(false);
  const [autoReminder, setAutoReminder] = useState(true);
  const [reminderDays, setReminderDays] = useState(5);
  const [usage, setUsage] = useState(null);
  const [code, setCode] = useState("");
  const [available, setAvailable] = useState(false);
  const [availLoaded, setAvailLoaded] = useState(false);

  useEffect(() => {
    api.get("/settings").then((r) => { setAutoReminder(r.data.auto_reminder); setReminderDays(r.data.auto_reminder_days); });
    api.get("/usage").then((r) => setUsage(r.data));
    api.get("/availability").then((r) => setAvailable(r.data.available_for_jobs)).catch(() => {}).finally(() => setAvailLoaded(true));
  }, []);

  const toggleAvailable = async (val) => {
    setAvailable(val);
    try {
      await api.put("/availability", { available: val });
      toast.success(val ? "Du är nu synlig för arbetsgivare" : "Du är inte längre synlig för arbetsgivare");
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); setAvailable(!val); }
  };

  const redeem = async () => {
    if (!code.trim()) return;
    try {
      const { data } = await api.post("/redeem", { code });
      toast.success(data.message || "Kod inlöst");
      setCode("");
      api.get("/usage").then((r) => setUsage(r.data));
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  const toggleReminder = async (val) => {
    setAutoReminder(val);
    try {
      await api.put("/settings", { auto_reminder: val });
      toast.success(val ? "Automatiska påminnelser på" : "Automatiska påminnelser av");
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); setAutoReminder(!val); }
  };

  const exportData = async () => {
    setBusy(true);
    try {
      const { data } = await api.get("/privacy/export");
      const blob = new Blob([JSON.stringify(data, null, 2)], { type: "application/json" });
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url; a.download = `hamn-mina-uppgifter.json`; a.click();
      URL.revokeObjectURL(url);
      toast.success("Dina uppgifter har exporterats");
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setBusy(false); }
  };

  const deleteAccount = async () => {
    try {
      await api.delete("/privacy/delete-account");
      toast.success("Ditt konto och all data har raderats");
      logout(); navigate("/");
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  const handleLogoutAll = async () => {
    setLoggingOutAll(true);
    try {
      await logoutAll();
      toast.success("Alla andra enheter har loggats ut. Du är fortfarande inloggad här.");
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setLoggingOutAll(false); }
  };

  return (
    <div>
      <PageHeader title="Inställningar och integritet" description="HAMN är byggt enligt privacy-by-design. Dina identitetsuppgifter, din profil och din CV-data hålls separerade. AI får bara den data som behövs för den aktuella uppgiften." />

      <Card className="rounded-2xl border-border p-6 mb-6" data-testid="availability-card">
        <div className="flex items-start justify-between gap-4">
          <div>
            <div className="flex items-center gap-2 font-head font-semibold text-lg"><Briefcase className="h-5 w-5 text-primary" /> Tillgänglig för jobb</div>
            <p className="text-sm text-muted-foreground mt-1 max-w-xl">
              När detta är på kan arbetsgivare hitta din <strong>anonyma</strong> profil via HAMN Match och matcha dig mot roller. De ser bara ditt HAMN-id – aldrig namn, ålder eller kön. Du väljer själv om du vill svara på en kontaktförfrågan och vad du delar.
            </p>
          </div>
          <Switch data-testid="availability-switch" checked={available} disabled={!availLoaded} onCheckedChange={toggleAvailable} />
        </div>
      </Card>

      <div className="mb-6"><SwishPricing audience="candidate" /></div>
      <div className="mb-6"><PaymentHistory /></div>

      <div className="grid gap-5 md:grid-cols-2">
        <Card className="rounded-2xl border-border p-6">
          <div className="flex h-10 w-10 items-center justify-center rounded-xl bg-primary/10 text-primary mb-4"><Download className="h-5 w-5" /></div>
          <h3 className="font-head font-semibold text-lg">Exportera dina uppgifter</h3>
          <p className="text-sm text-muted-foreground mt-1 mb-4">Ladda ner allt du lagt in i HAMN som en JSON-fil.</p>
          <Button className="rounded-full" onClick={exportData} disabled={busy} data-testid="export-btn"><Download className="h-4 w-4 mr-1.5" /> Exportera</Button>
        </Card>

        <Card className="rounded-2xl border-border p-6">
          <div className="flex h-10 w-10 items-center justify-center rounded-xl bg-primary/10 text-primary mb-4"><Pencil className="h-5 w-5" /></div>
          <h3 className="font-head font-semibold text-lg">Rättelse</h3>
          <p className="text-sm text-muted-foreground mt-1 mb-4">Du kan när som helst rätta dina uppgifter i din Masterprofil.</p>
          <Button variant="outline" className="rounded-full" onClick={() => navigate("/app/profil")} data-testid="edit-profile-btn">Gå till min profil</Button>
        </Card>

        <Card className="rounded-2xl border-border p-6">
          <div className="flex h-10 w-10 items-center justify-center rounded-xl bg-primary/10 text-primary mb-4"><ShieldCheck className="h-5 w-5" /></div>
          <h3 className="font-head font-semibold text-lg">Samtycke</h3>
          <p className="text-sm text-muted-foreground mt-1">Du samtyckte till behandling av dina uppgifter vid registrering. Du kan återkalla samtycket genom att radera ditt konto.</p>
        </Card>

        <Card className="rounded-2xl border-border p-6">
          <div className="flex h-10 w-10 items-center justify-center rounded-xl bg-primary/10 text-primary mb-4"><Lock className="h-5 w-5" /></div>
          <h3 className="font-head font-semibold text-lg">Känsliga uppgifter används aldrig</h3>
          <p className="text-sm text-muted-foreground mt-1">HAMN använder aldrig kön, etnicitet, religion, hälsa, politiska åsikter eller sexuell läggning för CV-optimering eller matchning.</p>
        </Card>
      </div>

      <Card className="rounded-2xl border-border p-6 mt-6">
        <div className="flex items-start justify-between gap-4">
          <div>
            <div className="flex items-center gap-2 font-head font-semibold text-lg"><BellRing className="h-5 w-5 text-primary" /> Automatiska referenspåminnelser</div>
            <p className="text-sm text-muted-foreground mt-1 max-w-xl">
              När detta är på skickar HAMN automatiskt en vänlig påminnelse till referenser som inte svarat efter {reminderDays} dagar (max 3 gånger). Kräver att e-post är konfigurerad.
            </p>
          </div>
          <Switch data-testid="auto-reminder-switch" checked={autoReminder} onCheckedChange={toggleReminder} />
        </div>
      </Card>

      <Card className="rounded-2xl border-border p-6 mt-6">
        <div className="flex items-start justify-between gap-4 flex-wrap">
          <div>
            <div className="flex items-center gap-2 font-head font-semibold text-lg"><Ticket className="h-5 w-5 text-primary" /> Din plan & accesskod</div>
            <p className="text-sm text-muted-foreground mt-1" data-testid="settings-plan-text">
              {usage ? (usage.unlimited
                ? "Du har obegränsad åtkomst – skapa så många CV:n du vill."
                : `Du har ${usage.remaining} av ${usage.cv_quota} gratis CV:n kvar. Därefter ${usage.price_per_cv} kr per CV eller ${usage.subscription_price} kr/mån för obegränsat.`) : ""}
            </p>
          </div>
          {usage && !usage.unlimited && (
            <div className="flex gap-2">
              <Input value={code} onChange={(e) => setCode(e.target.value.toUpperCase())} placeholder="Accesskod" className="w-40" data-testid="settings-redeem-input" />
              <Button className="rounded-full" onClick={redeem} data-testid="settings-redeem-btn">Lös in</Button>
            </div>
          )}
        </div>
      </Card>

      <Card className="rounded-2xl border-border p-6 mt-6" data-testid="security-card">
        <div className="flex items-start justify-between gap-4 flex-wrap">
          <div>
            <div className="flex items-center gap-2 font-head font-semibold text-lg"><LogOut className="h-5 w-5 text-primary" /> Logga ut alla enheter</div>
            <p className="text-sm text-muted-foreground mt-1 max-w-xl">
              Loggar ut ditt konto från alla enheter och webbläsare där du är inloggad. Använd detta om du misstänker att någon annan har åtkomst. Du förblir inloggad här.
            </p>
          </div>
          <AlertDialog>
            <AlertDialogTrigger asChild>
              <Button variant="outline" className="rounded-full" disabled={loggingOutAll} data-testid="logout-all-btn"><LogOut className="h-4 w-4 mr-1.5" /> Logga ut alla enheter</Button>
            </AlertDialogTrigger>
            <AlertDialogContent>
              <AlertDialogHeader>
                <AlertDialogTitle>Logga ut alla enheter?</AlertDialogTitle>
                <AlertDialogDescription>Alla andra inloggade enheter och webbläsare loggas ut direkt. Du behöver logga in på nytt där. Denna enhet påverkas inte.</AlertDialogDescription>
              </AlertDialogHeader>
              <AlertDialogFooter>
                <AlertDialogCancel>Avbryt</AlertDialogCancel>
                <AlertDialogAction data-testid="confirm-logout-all" onClick={handleLogoutAll}>Ja, logga ut överallt</AlertDialogAction>
              </AlertDialogFooter>
            </AlertDialogContent>
          </AlertDialog>
        </div>
      </Card>

      <Card className="rounded-2xl border-destructive/30 bg-destructive/5 p-6 mt-6">
        <div className="flex items-center gap-2 text-destructive font-head font-semibold text-lg mb-1"><Trash2 className="h-5 w-5" /> Radera konto</div>
        <p className="text-sm text-muted-foreground mb-4">Detta raderar ditt konto och all din data permanent – inklusive Masterprofil och alla CV:n. Åtgärden kan inte ångras.</p>
        <AlertDialog>
          <AlertDialogTrigger asChild>
            <Button variant="destructive" className="rounded-full" data-testid="delete-account-btn"><Trash2 className="h-4 w-4 mr-1.5" /> Radera mitt konto</Button>
          </AlertDialogTrigger>
          <AlertDialogContent>
            <AlertDialogHeader>
              <AlertDialogTitle>Är du helt säker?</AlertDialogTitle>
              <AlertDialogDescription>All din data raderas permanent. Detta kan inte ångras.</AlertDialogDescription>
            </AlertDialogHeader>
            <AlertDialogFooter>
              <AlertDialogCancel>Avbryt</AlertDialogCancel>
              <AlertDialogAction data-testid="confirm-delete-account" onClick={deleteAccount}>Ja, radera allt</AlertDialogAction>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialog>
      </Card>

      <div className="flex items-center gap-2 text-sm text-muted-foreground mt-6">
        <FileCheck2 className="h-4 w-4" /> Alla åtgärder loggas för din säkerhet (audit-logg).
      </div>
    </div>
  );
}
